/**
 * Comparison Store
 *
 * Manages school comparison state including:
 * - Selected schools for comparison
 * - Comparison view settings
 *
 * @module @schoolidb/store/comparisonStore
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { School } from '@schoolidb/types';

// ============================================================================
// Types
// ============================================================================

/**
 * Comparison school entry (minimal data for comparison)
 */
export interface ComparisonSchool {
  /** School ID */
  schoolId: string;
  /** School name */
  schoolName: string;
  /** Place ID */
  placeId?: string;
  /** Location */
  location?: {
    city?: string;
    state?: string;
  };
  /** Google rating */
  googleRating?: number;
  /** School type */
  schoolType?: string;
}

/**
 * Comparison state
 */
export interface ComparisonState {
  /** Selected schools for comparison */
  schools: ComparisonSchool[];
  /** Maximum schools allowed */
  maxSchools: number;
  /** Comparison categories to show */
  categories: string[];
}

/**
 * Comparison actions
 */
export interface ComparisonActions {
  /** Add school to comparison */
  addSchool: (school: ComparisonSchool | School) => boolean;
  /** Remove school from comparison */
  removeSchool: (schoolId: string) => void;
  /** Clear all schools */
  clearSchools: () => void;
  /** Check if school is in comparison */
  isInComparison: (schoolId: string) => boolean;
  /** Toggle school in comparison */
  toggleSchool: (school: ComparisonSchool | School) => boolean;
  /** Set comparison categories */
  setCategories: (categories: string[]) => void;
}

export type ComparisonStore = ComparisonState & ComparisonActions;

// ============================================================================
// Initial State
// ============================================================================

const defaultCategories = [
  'overview',
  'academics',
  'facilities',
  'fees',
  'ratings',
  'location',
];

const initialState: ComparisonState = {
  schools: [],
  maxSchools: 4,
  categories: defaultCategories,
};

// ============================================================================
// Helpers
// ============================================================================

/**
 * Convert School to ComparisonSchool
 */
function toComparisonSchool(school: ComparisonSchool | School): ComparisonSchool {
  if ('schoolId' in school && !('profile' in school)) {
    return school as ComparisonSchool;
  }
  
  const fullSchool = school as School;
  return {
    schoolId: fullSchool.schoolId,
    schoolName: fullSchool.schoolName,
    placeId: fullSchool.placeId,
    location: fullSchool.location ? {
      city: fullSchool.location.city,
      state: fullSchool.location.state,
    } : undefined,
    googleRating: fullSchool.googleRating,
    schoolType: fullSchool.schoolType,
  };
}

// ============================================================================
// Store
// ============================================================================

/**
 * Comparison store with persistence
 */
export const useComparisonStore = create<ComparisonStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      addSchool: (school) => {
        const state = get();
        if (state.schools.length >= state.maxSchools) {
          return false;
        }
        
        const comparisonSchool = toComparisonSchool(school);
        
        if (state.schools.some((s) => s.schoolId === comparisonSchool.schoolId)) {
          return false;
        }
        
        set({ schools: [...state.schools, comparisonSchool] });
        return true;
      },

      removeSchool: (schoolId) => {
        set((state) => ({
          schools: state.schools.filter((s) => s.schoolId !== schoolId),
        }));
      },

      clearSchools: () => {
        set({ schools: [] });
      },

      isInComparison: (schoolId) => {
        return get().schools.some((s) => s.schoolId === schoolId);
      },

      toggleSchool: (school) => {
        const comparisonSchool = toComparisonSchool(school);
        const state = get();
        
        if (state.isInComparison(comparisonSchool.schoolId)) {
          state.removeSchool(comparisonSchool.schoolId);
          return false;
        } else {
          return state.addSchool(comparisonSchool);
        }
      },

      setCategories: (categories) => {
        set({ categories });
      },
    }),
    {
      name: 'schoolidb-comparison',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        schools: state.schools,
        categories: state.categories,
      }),
    }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const selectComparisonSchools = (state: ComparisonStore) => state.schools;
export const selectComparisonCount = (state: ComparisonStore) => state.schools.length;
export const selectMaxSchools = (state: ComparisonStore) => state.maxSchools;
export const selectCategories = (state: ComparisonStore) => state.categories;
export const selectCanAddMore = (state: ComparisonStore) =>
  state.schools.length < state.maxSchools;
